/**
 * CoachFeedbackSection Component
 * Extracted from SessionDetailModal — coach feedback read-only view with concern flags.
 */

import { useState, useEffect, useCallback } from 'react';
import {
  Stack,
  Group,
  Text,
  Button,
  Card,
  Badge,
  Loader,
  Center,
  Divider,
  Box,
  Table,
  Textarea,
  ThemeIcon,
  Paper,
  Avatar,
  Tooltip,
} from '@mantine/core';
import { Flag, MessageCircle, Star } from 'react-feather';
import { sessionsApi } from '../../../../services/api/sessions';
import type {
  CoachSessionFeedbackWithAuthor,
  ConcernFlagWithDetails,
  ConcernType,
  ParticipantEngagementRating,
} from '../../../../services/api/coachPortal';
import { CONCERN_TYPE_CONFIG } from '../../../../config/concernTypeConfig';
import { ENGAGEMENT_RATING_MAP } from '../../../../config/engagementRatingConfig';
import {
  buildShortLabelMap,
  buildFullQuestionTextMap,
  getPartnerEngagementLabel,
  medleyCoachQuestionSet,
  partnerCoachQuestionSet,
  liveSessionCoachQuestionSet,
} from '../../../../config/coachFeedbackQuestionSets';
import { formatDateTime, formatDate } from '../../../../utils/formatters';

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

// MED-888: short labels and full question text resolved via the shared question-set
// config so the slide-out renders partner-coach answers with the correct headers
// (short label) and full question text on hover.
const SHORT_LABELS: Record<string, string> = {
  ...buildShortLabelMap(medleyCoachQuestionSet, partnerCoachQuestionSet, liveSessionCoachQuestionSet),
  // Legacy keys that pre-date the config refactor — keep displayable for back-compat.
  session_highlights: 'Session Highlights',
  challenges: 'Challenges',
  participant_observations: 'Participant Observations',
  next_session_focus: 'Next Session Focus',
};

const FULL_QUESTION_TEXT: Record<string, string> = {
  ...buildFullQuestionTextMap(medleyCoachQuestionSet, partnerCoachQuestionSet, liveSessionCoachQuestionSet),
  session_highlights: 'What were the highlights of this session?',
  challenges: 'What challenges or difficulties came up?',
  participant_observations: 'Any notable observations about individual participants?',
  next_session_focus: 'What should be the focus for the next session?',
};

const PARTNER_KEY_PREFIX = 'partner_';
const PARTNER_ENGAGEMENT_KEY = 'partner_group_engagement';

interface CoachFeedbackSectionProps {
  cohortId: string;
  sessionId: string;
  onFlagAddressed?: () => void;
}

export function CoachFeedbackSection({
  cohortId,
  sessionId,
  onFlagAddressed,
}: CoachFeedbackSectionProps) {
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState<CoachSessionFeedbackWithAuthor[]>([]);
  const [concernFlags, setConcernFlags] = useState<ConcernFlagWithDetails[]>([]);
  const [engagementRatings, setEngagementRatings] = useState<ParticipantEngagementRating[]>([]);
  const [addressingFlagId, setAddressingFlagId] = useState<string | null>(null);
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await sessionsApi.getCoachFeedback(cohortId, sessionId);
      setFeedback(data.feedback);
      setConcernFlags(data.concernFlags);
      setEngagementRatings(data.engagementRatings || []);
    } catch {
      // Silent fail - will show empty state
    } finally {
      setLoading(false);
    }
  }, [cohortId, sessionId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleMarkAddressed = async (flagId: string) => {
    setSaving(true);
    try {
      const updated = await sessionsApi.markConcernFlagAddressed(
        cohortId,
        sessionId,
        flagId,
        resolutionNotes || undefined,
      );
      setConcernFlags((prev) => prev.map((f) => (f.id === flagId ? updated : f)));
      setAddressingFlagId(null);
      setResolutionNotes('');
      onFlagAddressed?.();
    } catch {
      // Error handling - keeping it simple
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Center py="xl">
        <Loader size="sm" />
      </Center>
    );
  }

  return (
    <Stack gap="md">
      {feedback.length === 0 ? (
        <Paper withBorder p="lg" style={{ borderStyle: 'dashed' }}>
          <Stack align="center" gap="sm">
            <ThemeIcon size={40} radius="xl" variant="light" color="gray">
              <MessageCircle size={20} />
            </ThemeIcon>
            <Text size="sm" c="dimmed" ta="center">
              No coach feedback submitted yet for this session.
            </Text>
          </Stack>
        </Paper>
      ) : (
        feedback.map((fb) => (
          <Card key={fb.id} withBorder p="md">
            <Group gap="sm" mb="md">
              <Avatar size="sm" radius="xl" color="blue">
                {getInitials(fb.coach_name)}
              </Avatar>
              <div>
                <Text size="sm" fw={500}>
                  {fb.coach_name}
                </Text>
                <Text size="xs" c="dimmed">
                  Submitted {formatDateTime(fb.submitted_at)}
                </Text>
              </div>
            </Group>

            <Stack gap="sm">
              {Object.entries(fb.responses).map(([key, value]) => {
                if (value === null || value === undefined || value === '') return null;
                // MED-888: partner Q5 persists as numeric — render its friendly label.
                const displayValue =
                  key === PARTNER_ENGAGEMENT_KEY
                    ? getPartnerEngagementLabel(value as number | string)
                    : String(value);
                const shortLabel = SHORT_LABELS[key] || key;
                const fullText = FULL_QUESTION_TEXT[key];
                const isPartnerKey = key.startsWith(PARTNER_KEY_PREFIX);
                const labelNode = (
                  <Text size="xs" fw={600} c="dimmed" mb={2}>
                    {shortLabel}
                  </Text>
                );
                return (
                  <div key={key}>
                    {fullText && isPartnerKey ? (
                      <Tooltip
                        label={fullText}
                        withArrow
                        multiline
                        w={320}
                        position="top-start"
                      >
                        {labelNode}
                      </Tooltip>
                    ) : (
                      labelNode
                    )}
                    <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
                      {displayValue}
                    </Text>
                  </div>
                );
              })}
            </Stack>
          </Card>
        ))
      )}

      {/* Participant Engagement Ratings - show when any ratings exist */}
      <>
        <Divider my="xs" />

        <Group gap="sm">
          <Star size={16} />
          <Text fw={600} size="sm">
            Participant Engagement Ratings
          </Text>
        </Group>

        {engagementRatings.length === 0 ? (
          <Text size="sm" c="dimmed">
            No engagement ratings submitted for this session.
          </Text>
        ) : (
          <Table striped>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Participant</Table.Th>
                <Table.Th>Rating</Table.Th>
                <Table.Th>Level</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {engagementRatings.map((er) => {
                const config = ENGAGEMENT_RATING_MAP.get(er.rating);
                return (
                  <Table.Tr key={er.id}>
                    <Table.Td>
                      <Text size="sm" fw={500}>
                        {er.participant_name}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Group gap={2}>
                        {[1, 2, 3, 4, 5].map((star) => (
                          <Star
                            key={star}
                            size={14}
                            fill={star <= er.rating ? 'var(--mantine-color-yellow-5)' : 'none'}
                            color={
                              star <= er.rating
                                ? 'var(--mantine-color-yellow-5)'
                                : 'var(--mantine-color-gray-4)'
                            }
                          />
                        ))}
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Badge size="sm" color={config?.color || 'gray'} variant="light">
                        {config?.label || `${er.rating}/5`}
                      </Badge>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        )}
      </>

      <Divider my="xs" />

      <Group gap="sm">
        <Flag size={16} />
        <Text fw={600} size="sm">
          Participant Concern Flags ({concernFlags.length})
        </Text>
      </Group>

      {concernFlags.length === 0 ? (
        <Text size="sm" c="dimmed">
          No concern flags raised for this session.
        </Text>
      ) : (
        <Stack gap="sm">
          {concernFlags.map((flag) => {
            const typeConfig = CONCERN_TYPE_CONFIG[flag.concern_type as ConcernType];
            const isAddressing = addressingFlagId === flag.id;

            return (
              <Card
                key={flag.id}
                withBorder
                p="sm"
                style={{
                  borderColor:
                    flag.status === 'open'
                      ? 'var(--mantine-color-orange-4)'
                      : 'var(--mantine-color-gray-3)',
                  backgroundColor:
                    flag.status === 'open'
                      ? 'var(--mantine-color-orange-0)'
                      : undefined,
                }}
              >
                <Group justify="space-between" mb="xs">
                  <Group gap="sm">
                    <Badge
                      size="sm"
                      color={flag.status === 'open' ? 'orange' : 'green'}
                      variant="light"
                    >
                      {flag.status === 'open' ? 'Open' : 'Addressed'}
                    </Badge>
                    <Badge size="sm" variant="light" color="blue">
                      {typeConfig?.label || flag.concern_type}
                    </Badge>
                  </Group>
                  {flag.status === 'open' && !isAddressing && (
                    <Button
                      size="xs"
                      variant="light"
                      color="green"
                      onClick={() => setAddressingFlagId(flag.id)}
                    >
                      Mark Addressed
                    </Button>
                  )}
                </Group>

                <Group gap="lg" mb="xs">
                  <Text size="xs" c="dimmed">
                    <strong>Participant:</strong> {flag.participant_name}
                  </Text>
                  <Text size="xs" c="dimmed">
                    <strong>Flagged by:</strong> {flag.coach_name}
                  </Text>
                </Group>

                {flag.notes && (
                  <Text size="sm" mb="xs">
                    {flag.notes}
                  </Text>
                )}

                {flag.status === 'addressed' && (
                  <Box
                    p="xs"
                    mt="xs"
                    style={{
                      backgroundColor: 'var(--mantine-color-green-0)',
                      borderRadius: 'var(--mantine-radius-sm)',
                    }}
                  >
                    <Text size="xs" c="dimmed">
                      Addressed by {flag.addressed_by_name} on{' '}
                      {flag.addressed_at ? formatDate(flag.addressed_at) : '—'}
                    </Text>
                    {flag.resolution_notes && (
                      <Text size="sm" mt={4}>
                        {flag.resolution_notes}
                      </Text>
                    )}
                  </Box>
                )}

                {isAddressing && (
                  <Box mt="sm">
                    <Textarea
                      placeholder="Resolution notes (optional)"
                      value={resolutionNotes}
                      onChange={(e) => setResolutionNotes(e.currentTarget.value)}
                      minRows={2}
                      size="sm"
                      mb="xs"
                    />
                    <Group gap="xs" justify="flex-end">
                      <Button
                        size="xs"
                        variant="subtle"
                        color="gray"
                        onClick={() => {
                          setAddressingFlagId(null);
                          setResolutionNotes('');
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        size="xs"
                        color="green"
                        onClick={() => handleMarkAddressed(flag.id)}
                        loading={saving}
                      >
                        Confirm
                      </Button>
                    </Group>
                  </Box>
                )}
              </Card>
            );
          })}
        </Stack>
      )}
    </Stack>
  );
}

export default CoachFeedbackSection;
